import { Checkbox } from '@components/Checkbox';
import { Box, Stack, styled, SxProps, Typography } from '@mui/material';
import { IPatientItem, selectionsApiSlice } from '@src/api';
import { border, getDisplayDate, getFullName, scrollBarSM } from '@src/utils';
import { FC, useState, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useLoadingContext } from '../ContentLoading';

const Cell = styled(Stack)(() => ({
  justifyContent: 'center',
  minHeight: '42px',
}));

const gridTemplateColumns = '1fr 2fr 6.8fr 2.9fr';

const Header = ({ allChecked, onChangeCheckbox }) => {
  return (
    <Stack sx={{ display: 'grid', gridTemplateColumns, borderBottom: border }}>
      <Cell>
        <Checkbox checked={allChecked} onChange={onChangeCheckbox} />
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Bold" lineHeight="18px">
          Patient ID
        </Typography>
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Bold" lineHeight="18px">
          Name
        </Typography>
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Bold" lineHeight="18px">
          Date of Birth
        </Typography>
      </Cell>
    </Stack>
  );
};

type PatientItemProps = {
  patients: IPatientItem[];
  index: number;
  checkedPatients: string[];
  sx: SxProps;
};

const PatientItem: FC<PatientItemProps> = ({ patients, index, checkedPatients, sx }) => {
  const { setValue } = useFormContext();
  const { id, patientId, birthDate, ...name } = patients[index];

  const onChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const newCheckedPatients = checked
      ? [...checkedPatients, id]
      : checkedPatients.filter(checkedId => checkedId !== id);

    setValue('patientIds', newCheckedPatients, { shouldValidate: true });
  };

  const checked = checkedPatients.includes(id);

  const fullName = getFullName(name);
  return (
    <Stack sx={{ display: 'grid', gridTemplateColumns, borderBottom: border, ...sx }}>
      <Cell>
        <Checkbox checked={checked} onChange={onChangeCheckbox} />
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Medium" lineHeight="18px">
          {patientId}
        </Typography>
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Medium" lineHeight="18px">
          {fullName}
        </Typography>
      </Cell>
      <Cell>
        <Typography color="grey.700" variant="label14Medium" lineHeight="18px">
          {getDisplayDate(birthDate)}
        </Typography>
      </Cell>
    </Stack>
  );
};

type PatientsProps = {
  selectionId: number;
};

export const Patients: FC<PatientsProps> = ({ selectionId }) => {
  const { data: patients } = selectionsApiSlice.useGetPatientsQuery(selectionId);
  const { setValue, watch } = useFormContext();
  const { setLoadingState } = useLoadingContext() || {};
  const [allChecked, setAllChecked] = useState(true);

  const patientIds = watch('patientIds');

  const parentRef = useRef(null);

  useEffect(() => {
    if (patients)
      setValue(
        'patientIds',
        patients.map(({ id }) => id),
        { shouldValidate: true },
      );
  }, [patients, setValue]);

  useEffect(() => {
    if (patients && patientIds) {
      setAllChecked(patientIds.length === patients.length);
      setLoadingState && setLoadingState(prev => ({ ...prev, patients: true }));
    }
  }, [patientIds, patients, setAllChecked, setLoadingState]);

  const rowVirtualizer = useVirtualizer({
    count: patients?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 42,
    paddingStart: 0,
    overscan: 5,
  });

  const onChangeCommonCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    const newCheckedPatients = checked ? patients?.map(({ id }) => id) : [];

    setValue('patientIds', newCheckedPatients, { shouldValidate: true });
  };
  if (!patients) return null;

  return (
    <>
      <Header allChecked={allChecked} onChangeCheckbox={onChangeCommonCheckbox} />
      <Box ref={parentRef} sx={scrollBarSM} overflow="auto" height="700px">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map(virtualItem => (
            <PatientItem
              key={virtualItem.key}
              checkedPatients={patientIds}
              patients={patients}
              index={virtualItem.index}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            />
          ))}
        </div>
      </Box>
    </>
  );
};
